import { Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Color, Masks } from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import {
    ACCOUNT_NUMBER_MAX_LENGTH,
    ROUTING_NUMBER_LENGTH
} from '../../../../../constants/bank.constants';
import { ManageBuyerService } from '../manage-buyer.service';
import { formatBpsInput, handleBpsInput } from './buyer-configuration-helpers';
import { AdminManageBuyerConfigurationService } from './buyer-configuration.service';

@Component({
    selector: 'admin-buyer-configuration-revenue-share',
    template: `
        <buyer-configuration-section
            [hasClickedSave]="hasClickedSave"
            sectionFormControlName="enableRevenueShare"
            [sectionFormGroup]="revenueShareFormGroup"
            sectionTitle="Revenue Share Configuration"
        >
            <form [formGroup]="revenueShareFormGroup" [style.padding]="'2rem 3rem'">
                <div class="row" [style.marginBottom]="'1rem'">
                    <div class="col-12 col-lg-6 order-md-1">
                        <lsq-label
                            label="Platform Revenue Share*"
                            subLabel="The revenue share a third party platform will receive. Set to percentage up to two decimal places."
                        ></lsq-label>
                    </div>
                    <div class="col-12 col-lg-6 order-md-3">
                        <lsq-input
                            [basicLabel]="true"
                            name="platformRevenueShare"
                            formControlName="platformRevenueShare"
                            [group]="revenueShareFormGroup"
                            [textFormatter]="formatBpsInput"
                            [handleKeypress]="handleBpsInput"
                            [mask]="bpsAsPercentMask"
                            [appendIcon]="[icons.FaPercent, color.GraphicGray]"
                        >
                        </lsq-input>
                    </div>
                    <div class="col-12 col-lg-6 order-md-2">
                        <lsq-label
                            label="LSQ Revenue Share*"
                            subLabel="The revenue share LSQ will receive. Set to percentage up to two decimal places."
                        ></lsq-label>
                    </div>
                    <div class="col-12 col-lg-6 order-md-4">
                        <lsq-input
                            [basicLabel]="true"
                            name="lsqRevenueShare"
                            formControlName="lsqRevenueShare"
                            [group]="revenueShareFormGroup"
                            [textFormatter]="formatBpsInput"
                            [handleKeypress]="handleBpsInput"
                            [mask]="bpsAsPercentMask"
                            [appendIcon]="[icons.FaPercent, color.GraphicGray]"
                        >
                        </lsq-input>
                    </div>
                </div>
                <div class="row" [style.marginBottom]="'1rem'">
                    <div class="col-12 col-lg-6 order-md-1">
                        <lsq-label
                            label="Primary Buyer Revenue Share*"
                            subLabel="What the buyer receives if they pay before the primary threshold. Set to percentage up to two decimal places."
                        ></lsq-label>
                    </div>
                    <div class="col-12 col-lg-6 order-md-3">
                        <lsq-input
                            [basicLabel]="true"
                            name="primaryBuyerRevenueShare"
                            formControlName="primaryBuyerRevenueShare"
                            [group]="revenueShareFormGroup"
                            [textFormatter]="formatBpsInput"
                            [handleKeypress]="handleBpsInput"
                            [mask]="bpsAsPercentMask"
                            [appendIcon]="[icons.FaPercent, color.GraphicGray]"
                        >
                        </lsq-input>
                    </div>
                    <div class="col-12 col-lg-6 order-md-2">
                        <lsq-label
                            [style.marginBottom]="'1.5rem'"
                            label="Secondary Buyer Revenue Share*"
                            subLabel="What the buyer will receive if they pay after the primary threshold, but before the secondary threshold. Set to whole percentage up to two decimal places."
                        ></lsq-label>
                    </div>
                    <div class="col-12 col-lg-6 order-md-4">
                        <lsq-input
                            [basicLabel]="true"
                            name="secondaryBuyerRevenueShare"
                            formControlName="secondaryBuyerRevenueShare"
                            [group]="revenueShareFormGroup"
                            [textFormatter]="formatBpsInput"
                            [handleKeypress]="handleBpsInput"
                            [mask]="bpsAsPercentMask"
                            [appendIcon]="[icons.FaPercent, color.GraphicGray]"
                        >
                        </lsq-input>
                    </div>
                </div>
                <div class="row" [style.marginBottom]="'1rem'">
                    <div class="col-12" [style.marginBottom]="'1rem'">
                        <lsq-label
                            label="Primary Due Date Threshold*"
                            subLabel="The number of days after invoice date that the buyer must pay by to receive the Primary Revenue Share. If revenue share and late fees are invoice terms based, input 0."
                        ></lsq-label>
                        <lsq-input
                            [basicLabel]="true"
                            name="primaryDueDateThreshold"
                            formControlName="primaryDueDateThreshold"
                            [group]="revenueShareFormGroup"
                            type="number"
                        >
                        </lsq-input>
                    </div>
                    <div class="col-12">
                        <lsq-label
                            label="Secondary Due Date Threshold*"
                            subLabel="The number of days after an invoice date the buyer must pay the invoice before they begin to incur late fees."
                        ></lsq-label>
                        <lsq-input
                            [basicLabel]="true"
                            name="secondaryDueDateThreshold"
                            formControlName="secondaryDueDateThreshold"
                            [group]="revenueShareFormGroup"
                            type="number"
                        >
                        </lsq-input>
                    </div>
                </div>
                <div class="divider"></div>

                <ng-container *ngIf="!!(bankAccount$ | async) && !(isUpdatingBankAccount$ | async)">
                    <p class="body" [style.marginBottom]="'2rem'">
                        The following bank account is the Buyer's default bank account, and is the
                        account that is used for Revenue Share. Editing the bank account here will
                        create a new bank account and set it as the default for the buyer.
                    </p>
                    <data-card [style.marginBottom]="'1rem'">
                        <p id="existingBankAccountName" class="label">
                            {{ (bankAccount$ | async).bankAccountName }} ({{
                                (bankAccount$ | async).bankAccountBankName
                            }})
                        </p>
                        <p id="existingBankAccountNumber">
                            <b>Account Number: </b>*****{{
                                (bankAccount$ | async).bankAccountNumberLastFour
                            }}
                        </p>
                        <p id="existingBankAccountRoutingNumber">
                            <b>Routing Number: </b>*****{{
                                (bankAccount$ | async).bankAccountRoutingNumberLastFour
                            }}
                        </p>
                        <lsq-button
                            appearance="transparent"
                            (buttonClick)="setIsUpdatingBankAccount(true)"
                        >
                            Edit
                        </lsq-button>
                    </data-card>
                </ng-container>

                <ng-container *ngIf="!(bankAccount$ | async) || (isUpdatingBankAccount$ | async)">
                    <div class="row">
                        <div class="col-12 " [style.marginBottom]="'1rem'">
                            <p class="body" [style.marginBottom]="'2rem'">
                                The bank account created here will be set as the Buyer's default
                                bank account, which will be used for Revenue Share.
                            </p>
                            <p
                                class="body"
                                *ngIf="isImplementation$ | async"
                                [style.marginBottom]="'2rem'"
                            >
                                Please note that while a buyer is still in the implementation
                                process it is not necessary to add the Bank Account information at
                                the same time as adding the revenue share configuration. However,
                                both must be filled out before completing the implementation.
                            </p>
                            <lsq-label
                                label="Exact Bank Account Name*"
                                subLabel="Enter the name for the bank account exactly as it appears from the bank or statement."
                            ></lsq-label>
                            <lsq-input
                                [basicLabel]="true"
                                name="bankAccountName"
                                formControlName="bankAccountName"
                                [group]="revenueShareFormGroup"
                            >
                            </lsq-input>
                        </div>
                    </div>
                    <div class="row" [style.marginBottom]="'1rem'">
                        <div class="col-12 col-lg-6 order-md-1">
                            <lsq-label
                                label="Account Number*"
                                subLabel="The 6 to 16-digit account number of the bank account."
                            ></lsq-label>
                        </div>
                        <div class="col-12 col-lg-6 order-md-3">
                            <lsq-input
                                [basicLabel]="true"
                                name="bankAccountNumber"
                                formControlName="bankAccountNumber"
                                [group]="revenueShareFormGroup"
                                [maxLength]="accountNumberMaxLength"
                                [noPaste]="true"
                                [digitsOnly]="true"
                                [type]="'number'"
                            >
                            </lsq-input>
                        </div>
                        <div class="col-12 col-lg-6 order-md-2">
                            <lsq-label
                                label="Verify Account Number*"
                                subLabel="Please re-enter the account number."
                            ></lsq-label>
                        </div>
                        <div class="col-12 col-lg-6 order-md-4">
                            <lsq-input
                                [basicLabel]="true"
                                name="verifyBankAccountNumber"
                                formControlName="verifyBankAccountNumber"
                                [group]="revenueShareFormGroup"
                                [maxLength]="accountNumberMaxLength"
                                [noPaste]="true"
                                [type]="'number'"
                                [digitsOnly]="true"
                            >
                            </lsq-input>
                        </div>
                    </div>
                    <div class="row" [style.marginBottom]="'1rem'">
                        <div class="col-12 col-lg-6 order-md-1">
                            <lsq-label
                                label="Transit / Routing Number*"
                                subLabel="The 9-digit routing number of the bank."
                            ></lsq-label>
                        </div>
                        <div class="col-12 col-lg-6 order-md-3">
                            <lsq-input
                                [basicLabel]="true"
                                name="routingNumber"
                                formControlName="routingNumber"
                                [group]="revenueShareFormGroup"
                                [maxLength]="routingNumberMaxLength"
                                [type]="'number'"
                                [digitsOnly]="true"
                            >
                            </lsq-input>
                        </div>
                        <div class="col-12 col-lg-6 order-md-2">
                            <lsq-label
                                label="Bank Name*"
                                subLabel="The name of the bank."
                            ></lsq-label>
                        </div>
                        <div class="col-12 col-lg-6 order-md-4">
                            <lsq-input
                                [basicLabel]="true"
                                name="bankName"
                                formControlName="bankName"
                                [group]="revenueShareFormGroup"
                            >
                            </lsq-input>
                        </div>
                    </div>
                    <div class="row" [style.marginBottom]="'1rem'">
                        <div class="col-12 col-lg-6 " [style.marginBottom]="'1rem'">
                            <lsq-button
                                *ngIf="!!(bankAccount$ | async)"
                                appearance="secondary"
                                (buttonClick)="setIsUpdatingBankAccount(false)"
                            >
                                Cancel
                            </lsq-button>
                        </div>
                    </div>
                </ng-container>
            </form>
        </buyer-configuration-section>
    `
})
export class AdminBuyerConfigurationRevenueShareComponent {
    @Input() public revenueShareFormGroup: FormGroup;
    @Input() public hasClickedSave: boolean;

    public readonly accountNumberMaxLength = ACCOUNT_NUMBER_MAX_LENGTH;
    public readonly routingNumberMaxLength = ROUTING_NUMBER_LENGTH;

    public readonly icons = Icons;
    public readonly color = Color;
    public bpsAsPercentMask = Masks.BpsAsPercentMask;
    public formatBpsInput = formatBpsInput;
    public handleBpsInput = handleBpsInput;

    public bankAccount$ = this._adminManageBuyerConfigurationService.revenueShareBankAccount$;
    public isUpdatingBankAccount$ = this._adminManageBuyerConfigurationService
        .isUpdatingRevenueShareBankAccount$;

    public isImplementation$ = this._adminManageBuyersService.isImplementation$;

    constructor(
        private _adminManageBuyerConfigurationService: AdminManageBuyerConfigurationService,
        private _adminManageBuyersService: ManageBuyerService
    ) {}

    public setIsUpdatingBankAccount(isUpdating: boolean): void {
        this._adminManageBuyerConfigurationService.setIsUpdatingRevenueShareBankAccount(isUpdating);
    }
}
